import React, { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import SelectDropdown from "../forms/selectDropdown";
import { useDispatch } from "react-redux";
import LoaderModal from "./loader-modal";
import { errorToast, successToast } from "../notifications/toast";
import { routesListCreditAction, createCreditAction } from "../../redux/actions/creditAction";
import { storesDropdownAction } from "../../redux/actions/routeAction";

const CreateCreditModal = ({ isOpen, onClose, fetchCredits }) => {
    const dispatch = useDispatch();
    const [storeOptions, setStoreOptions] = useState([]);
    const [routeOptions, setRouteOptions] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedStoreId, setSelectedStoreId] = useState(null)

    const initialValues = {
        store_id: "",
        route_id: "",
        total_amount: "",
        paid_amount: "",
        pending_amount: ""
    };

    const validationSchema = Yup.object({
        store_id: Yup.string().required("Store is required"),
        route_id: Yup.string().required("Route is required"),
        total_amount: Yup.number().typeError("Must be a number").required("Required").min(0),
        paid_amount: Yup.number().typeError("Must be a number").required("Required").min(0),
        pending_amount: Yup.number().typeError("Must be a number").required("Required").min(0),
    });

    const onError = (data) => {
        const errorMsg = data?.data?.message || "Unable to process request";
        errorToast(errorMsg);
        setModalLoading(false);
    };

    useEffect(() => {
        if (isOpen) {
            dispatch(
                storesDropdownAction(
                    {},
                    (data) => {
                        const formatted = data.data.data.map(item => ({ value: item.id, label: item.name }));
                        setStoreOptions(formatted);
                    },
                    onError
                )
            );
            dispatch(
                routesListCreditAction(
                    { store_id: selectedStoreId },
                    (data) => {
                        const formatted = data.data.data.map(item => ({ value: item.id, label: item.name }));
                        setRouteOptions(formatted);
                    },
                    onError
                )
            );
        }
    }, [isOpen, selectedStoreId]);

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => { }}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
                    <Dialog.Panel className="bg-white rounded-lg p-6 shadow-lg max-w-md w-full">
                        <Dialog.Title className="text-lg font-medium leading-6 text-primary">
                            Create Credit Entry
                        </Dialog.Title>
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={(values, { resetForm }) => {
                                setModalLoading(true);
                                const dispatchValues = {
                                    store_id: values.store_id,
                                    creditRoutes: [{
                                        route_id: values.route_id,
                                        total_amount: values.total_amount,
                                        paid_amount: values.paid_amount,
                                        pending_amount: values.pending_amount
                                    }]
                                }
                                dispatch(
                                    createCreditAction(dispatchValues,
                                        (data) => {
                                            successToast(data.data.message);
                                            setModalLoading(false);
                                            resetForm();
                                            onClose();
                                            fetchCredits();
                                        },
                                        onError
                                    )
                                );
                            }}
                        >
                            {({ setFieldValue, values }) => {
                                const handleAmountChange = (e, fieldName) => {
                                    const newValue = e.target.value;
                                    setFieldValue(fieldName, newValue);

                                    const total = fieldName === "total_amount" ? parseFloat(newValue) : parseFloat(values.total_amount);
                                    const paid = fieldName === "paid_amount" ? parseFloat(newValue) : parseFloat(values.paid_amount);
                                    const pending = total - paid;

                                    if (!isNaN(pending)) {
                                        setFieldValue("pending_amount", pending.toFixed(2));
                                    }
                                };


                                return (
                                    <Form>
                                        <div className="mt-4">
                                            <div className="flex justify-between">
                                                <label className="block text-start">Store</label>
                                                <ErrorMessage name="store_id" component="div" className="text-red-500 text-sm text-start" />
                                            </div>
                                            <SelectDropdown
                                                options={storeOptions}
                                                value={values.store_id}
                                                onChange={(name, value) => { setFieldValue("store_id", value); setSelectedStoreId(value) }}
                                                placeholder="Select Store"
                                                name="store_id"
                                                className="input-field w-full"
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <div className="flex justify-between">
                                                <label className="block text-start">Route</label>
                                                <ErrorMessage name="route_id" component="div" className="text-red-500 text-sm text-start" />
                                            </div>
                                            <SelectDropdown
                                                options={routeOptions}
                                                value={values.route_id}
                                                onChange={(name, value) => setFieldValue("route_id", value)}
                                                placeholder="Select Route"
                                                name="route_id"
                                                className="input-field w-full"
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <div className="flex justify-between">
                                                <label className="block text-start">Total Amount</label>
                                                <ErrorMessage name="total_amount" component="div" className="text-red-500 text-sm text-start" />
                                            </div>
                                            <Field
                                                type="text"
                                                name="total_amount"
                                                className="input-field w-full mt-1"
                                                placeholder="Enter total amount"
                                                onChange={(e) => handleAmountChange(e, "total_amount")}
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <div className="flex justify-between">
                                                <label className="block text-start">Paid Amount</label>
                                                <ErrorMessage name="paid_amount" component="div" className="text-red-500 text-sm text-start" />
                                            </div>
                                            <Field
                                                type="text"
                                                name="paid_amount"
                                                className="input-field w-full mt-1"
                                                placeholder="Enter paid amount"
                                                onChange={(e) => handleAmountChange(e, "paid_amount")}
                                            />
                                        </div>

                                        <div className="mt-4">
                                            <div className="flex justify-between">
                                                <label className="block text-start">Pending Amount</label>
                                                <ErrorMessage name="pending_amount" component="div" className="text-red-500 text-sm text-start" />
                                            </div>
                                            <Field
                                                type="text"
                                                name="pending_amount"
                                                className="input-field w-full mt-1"
                                                placeholder="Pending amount"
                                            />
                                        </div>

                                        <div className="mt-6 flex justify-between gap-3">
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </Dialog.Panel>

                    {modalLoading && <LoaderModal />}
                </div>
            </Dialog>
        </Transition>
    );
};

export default CreateCreditModal;
